import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense, ComponentType } from 'react';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { GuestGatePage } from '@/components/GuestGatePage';
import { PageTransition } from '@/components/PageTransition';
import { useAuth } from '@/contexts/AuthContext';
import { isDynamicImportFailure, recoverFromChunkLoadError } from '@/lib/chunkLoadRecovery';
import NotFound from '@/pages/NotFound';

function LazyLoadErrorFallback({ autoRecovering = false }: { autoRecovering?: boolean }) {
  const handleRefresh = () => {
    void recoverFromChunkLoadError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-sm space-y-4">
        <p className="text-base text-foreground">
          {autoRecovering ? '앱을 최신 버전으로 다시 불러오는 중이에요…' : 'Failed to load the page.'}
        </p>
        <button
          onClick={handleRefresh}
          className="inline-flex min-w-28 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          {autoRecovering ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.error('[LazyLoad] Dynamic import failed, retrying...', error);
      await new Promise(resolve => setTimeout(resolve, 1000));
      try {
        return await componentImport();
      } catch (retryError) {
        console.error('[LazyLoad] Retry also failed:', retryError);

        const shouldRecover = isDynamicImportFailure(retryError);
        if (shouldRecover) {
          void recoverFromChunkLoadError();
        }

        return {
          default: (() => <LazyLoadErrorFallback autoRecovering={shouldRecover} />) as unknown as T
        };
      }
    }
  });
}

// Lazy load all pages
const AdminAnalyticsDashboard = lazyWithRetry(() => import('../pages/AdminAnalyticsDashboard'));
const AdminEventDashboard = lazyWithRetry(() => import('../pages/AdminEventDashboard'));
const AdminTrustDashboard = lazyWithRetry(() => import('../pages/AdminTrustDashboard'));
const AdminReportsDashboard = lazyWithRetry(() => import('../pages/AdminReportsDashboard'));
const AdminForumLabelsDashboard = lazyWithRetry(() => import('../pages/AdminForumLabelsDashboard'));
const TrustScoreInfo = lazyWithRetry(() => import('../pages/TrustScoreInfo'));
const FeedbackGuide = lazyWithRetry(() => import('../pages/FeedbackGuide'));
const EventCalendar = lazyWithRetry(() => import('../pages/EventCalendar'));
const Index = lazyWithRetry(() => import('../pages/Index'));
const LocalLife = lazyWithRetry(() => import('../pages/LocalLife'));
const PostDetail = lazyWithRetry(() => import('../pages/PostDetail'));
const NearMe = lazyWithRetry(() => import('../pages/NearMe'));
const Chat = lazyWithRetry(() => import('../pages/Chat'));
const ChatDetail = lazyWithRetry(() => import('../pages/ChatDetail'));
const GroupChatDetail = lazyWithRetry(() => import('../pages/GroupChatDetail'));
const MyGem = lazyWithRetry(() => import('../pages/MyGem'));
const MyListings = lazyWithRetry(() => import('../pages/MyListings'));
const MyReports = lazyWithRetry(() => import('../pages/MyReports'));
const Watchlist = lazyWithRetry(() => import('../pages/Watchlist'));
const Settings = lazyWithRetry(() => import('../pages/Settings'));
const ListingDetail = lazyWithRetry(() => import('../pages/ListingDetail'));
const MyComments = lazyWithRetry(() => import('../pages/MyComments'));
const SetYourHood = lazyWithRetry(() => import('../pages/SetYourHood'));
const AdminAdDashboard = lazyWithRetry(() => import('../pages/AdminAdDashboard'));
const BusinessAdSubmission = lazyWithRetry(() => import('../pages/BusinessAdSubmission'));
const SmsOptIn = lazyWithRetry(() => import('../pages/SmsOptIn'));
const DealsHistory = lazyWithRetry(() => import('../pages/DealsHistory'));
const FavoriteEvents = lazyWithRetry(() => import('../pages/FavoriteEvents'));
const MeetingDetail = lazyWithRetry(() => import('../pages/MeetingDetail'));
const MeetingFeedback = lazyWithRetry(() => import('../pages/MeetingFeedback'));
const MeetingQRScreen = lazyWithRetry(() => import('../components/MeetingQRScreen'));
const MeetGroupDetail = lazyWithRetry(() => import('../pages/MeetGroupDetail'));
const MeetEventDetail = lazyWithRetry(() => import('../pages/MeetEventDetail'));
const Install = lazyWithRetry(() => import('../pages/Install'));
const ManageAccount = lazyWithRetry(() => import('../pages/ManageAccount'));
const MyPurchases = lazyWithRetry(() => import('../pages/MyPurchases'));
const LinkedInBackground = lazyWithRetry(() => import('../pages/LinkedInBackground'));
const SurveyPreview = lazyWithRetry(() => import('../pages/SurveyPreview'));
const TutorialDemo = lazyWithRetry(() => import('../pages/TutorialDemo'));
const DarkModePreview = lazyWithRetry(() => import('../pages/DarkModePreview'));
const MyGemPreviewUncompleted = lazyWithRetry(() => import('../pages/MyGemPreviewUncompleted'));
const EventsDirectory = lazyWithRetry(() => import('../pages/EventsDirectory'));
const MyGroups = lazyWithRetry(() => import('../pages/MyGroups'));
const EventDetailPublic = lazyWithRetry(() => import('../pages/EventDetailPublic'));
const FleaMarket = lazyWithRetry(() => import('../pages/FleaMarket'));
const FleaMarketDetail = lazyWithRetry(() => import('../pages/FleaMarketDetail'));
const Auth = lazyWithRetry(() => import('../pages/Auth'));
const SellerProfilePage = lazyWithRetry(() => import('../pages/SellerProfilePage'));
const LocalLifeProfilePage = lazyWithRetry(() => import('../pages/LocalLifeProfilePage'));

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 spinner-gradient"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/flea-market" replace />;
  }
  return <Index />;
}

function LoadingFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="text-center space-y-4">
        <div className="h-8 w-8 animate-spin rounded-full spinner-gradient mx-auto" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export function AnimatedRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <AnimatePresence mode="wait" initial={false}>
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            <Route path="/auth" element={<Auth />} />
            <Route path="/install" element={<Install />} />
            <Route path="/linkedin-bg" element={<LinkedInBackground />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/survey" element={<SurveyPreview />} />
            <Route path="/tutorial-demo" element={<TutorialDemo />} />
            <Route path="/dark-mode-preview" element={<DarkModePreview />} />
            <Route path="/my-gem-preview" element={<MyGemPreviewUncompleted />} />
            <Route path="/set-your-hood" element={<ProtectedRoute><SetYourHood /></ProtectedRoute>} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/local-life" element={<LocalLife />} />
            <Route path="/local-life/profile/:id" element={<LocalLifeProfilePage />} />
            <Route path="/local-life/:id" element={<PostDetail />} />
            <Route path="/meet/:id" element={<MeetGroupDetail />} />
            <Route path="/my-groups" element={<ProtectedRoute><MyGroups /></ProtectedRoute>} />
            <Route path="/meet/:id/event/:eventId" element={<ProtectedRoute><MeetEventDetail /></ProtectedRoute>} />
            <Route path="/near-me" element={<NearMe />} />
            <Route path="/near-me/favorites" element={<ProtectedRoute><FavoriteEvents /></ProtectedRoute>} />
            <Route path="/near-me/calendar" element={<ProtectedRoute><EventCalendar /></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute><AdminEventDashboard /></ProtectedRoute>} />
            <Route path="/deals" element={<ProtectedRoute><DealsHistory /></ProtectedRoute>} />
            <Route path="/chat" element={<GuestGatePage actionLabel="use chat"><Chat /></GuestGatePage>} />
            <Route path="/chat/:id" element={<ProtectedRoute><ChatDetail /></ProtectedRoute>} />
            <Route path="/group-chat/:id" element={<ProtectedRoute><GroupChatDetail /></ProtectedRoute>} />
            <Route path="/meeting/:id" element={<ProtectedRoute><MeetingDetail /></ProtectedRoute>} />
            <Route path="/meeting/:id/qr" element={<ProtectedRoute><MeetingQRScreen /></ProtectedRoute>} />
            <Route path="/meeting/:id/feedback" element={<ProtectedRoute><MeetingFeedback /></ProtectedRoute>} />
            <Route path="/review-demo" element={<ProtectedRoute><MeetingFeedback /></ProtectedRoute>} />
            <Route path="/my-gem" element={<GuestGatePage actionLabel="view your profile"><MyGem /></GuestGatePage>} />
            <Route path="/my-gem/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
            <Route path="/my-gem/my-purchases" element={<ProtectedRoute><DealsHistory /></ProtectedRoute>} />
            <Route path="/my-gem/my-reports" element={<ProtectedRoute><MyReports /></ProtectedRoute>} />
            <Route path="/my-gem/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
            <Route path="/my-gem/my-comments" element={<ProtectedRoute><MyComments /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/settings/account" element={<ProtectedRoute><ManageAccount /></ProtectedRoute>} />
            <Route path="/listing/:id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
            <Route path="/user/:id" element={<SellerProfilePage />} />
            <Route path="/admin/ads" element={<ProtectedRoute><AdminAdDashboard /></ProtectedRoute>} />
            <Route path="/admin/trust" element={<ProtectedRoute><AdminTrustDashboard /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute><AdminReportsDashboard /></ProtectedRoute>} />
            <Route path="/admin/forum-labels" element={<ProtectedRoute><AdminForumLabelsDashboard /></ProtectedRoute>} />
            <Route path="/trust-score" element={<ProtectedRoute><TrustScoreInfo /></ProtectedRoute>} />
            <Route path="/feedback-guide" element={<ProtectedRoute><FeedbackGuide /></ProtectedRoute>} />
            <Route path="/business/advertise" element={<ProtectedRoute><BusinessAdSubmission /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/flea-market" element={<FleaMarket />} />
            <Route path="/flea-market/:id" element={<FleaMarketDetail />} />
            <Route path="/events" element={<EventsDirectory />} />
            <Route path="/events/detail/:id/:slug" element={<EventDetailPublic />} />
            <Route path="/events/detail/:id" element={<EventDetailPublic />} />
            <Route path="/events/:category" element={<EventsDirectory />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </AnimatePresence>
    </Suspense>
  );
}
